/**
 * Helpers for the admin tenants billing summary view.
 *
 * Powers the Revenue / Cost / Profit / Margin columns on the
 * `?page=billing-admin&tab=tenants` table — accounting-standard semantics:
 *
 *   Revenue = subscription proxy + topup + manual_grant + manual_deduct − refund
 *   Cost    = provider_cost + platform_paid_cost (COGS — what we paid the providers,
 *             for the tenant's own usage and for what the platform paid on its behalf)
 *   Profit  = Revenue − Cost            (gross profit, absolute USD)
 *   Margin  = Profit / Revenue × 100%   (gross margin, percent — null when Revenue ≤ 0)
 *
 * `total_charge_microusd` (the wallet "Spent" tally) is intentionally NOT in
 * the formulas — it's an internal wallet bookkeeping flow, not cash. The summary
 * still carries it for possible future drill-down, but the table doesn't
 * surface it as its own column.
 *
 * The rows come from `loadAdminTenantBillingSummary`; this module stays free of
 * server imports because the Tenants tab renders with it.
 */

import type { AdminTenantBillingSummary } from './types'

/**
 * Gross revenue per tenant (accounting-standard top-line):
 *   subscription proxy + topup + manual_grant + manual_deduct − refund.
 *
 * Manual entries are folded in here because admins record `manual_grant` for
 * off-Stripe payments (bank transfer / cash) — that's still revenue, just
 * through a different channel. `manual_deduct` is ledger-signed-negative
 * (`wallet_debit` writes `-p_amount`), so adding it directly subtracts the
 * clawback. Refund is subtracted because the money was returned.
 *
 * Caveat: `subscription_revenue_microusd` is a wallet-credit proxy
 * (`initial_grant + payment_credit`), not the full Stripe-billed invoice.
 * Tenants on plans with `payment_to_balance_microusd = 0` will show $0
 * subscription revenue here even though Stripe invoiced them — that amount
 * lives only on Stripe. The Revenue column tooltip flags this explicitly.
 */
export function computeRevenueMicrousd(
  s: Pick<
    AdminTenantBillingSummary,
    | 'subscription_revenue_microusd'
    | 'topup_revenue_microusd'
    | 'manual_grant_microusd'
    | 'manual_deduct_microusd'
    | 'refund_microusd'
  >,
): number {
  return (
    s.subscription_revenue_microusd
    + s.topup_revenue_microusd
    + s.manual_grant_microusd
    + s.manual_deduct_microusd
    - s.refund_microusd
  )
}

/**
 * Provider cost (COGS) per tenant: what the tenant's own usage cost us plus what the
 * platform paid on its behalf. The latter brings no revenue, so it lands on the cost
 * side alone.
 */
export function computeCostMicrousd(
  s: Pick<AdminTenantBillingSummary, 'total_provider_cost_microusd' | 'platform_paid_cost_microusd'>,
): number {
  return s.total_provider_cost_microusd + s.platform_paid_cost_microusd
}

type ProfitInputs = Pick<
  AdminTenantBillingSummary,
  | 'subscription_revenue_microusd'
  | 'topup_revenue_microusd'
  | 'manual_grant_microusd'
  | 'manual_deduct_microusd'
  | 'refund_microusd'
  | 'total_provider_cost_microusd'
  | 'platform_paid_cost_microusd'
>

/**
 * Gross profit per tenant: `Revenue − Cost`. Absolute USD figure (microUSD
 * here). Operational overhead (servers, salaries, marketing) is not modelled,
 * so this is a contribution profit, not a P&L bottom line.
 */
export function computeProfitMicrousd(s: ProfitInputs): number {
  return computeRevenueMicrousd(s) - computeCostMicrousd(s)
}

/**
 * Gross margin as a fraction (e.g. 0.42 = 42%): `Profit / Revenue`. Returns
 * `null` when Revenue is non-positive — division by zero (no income) or a
 * degenerate negative-revenue case (refunds exceeded all inflows). UI surfaces
 * the null as a dash. Multiply by 100 for percent display.
 *
 * Bounded above by 1.0 (cost = 0 ⇒ 100% margin); unbounded below
 * (cost > revenue ⇒ negative margin).
 */
export function computeMarginFraction(s: ProfitInputs): number | null {
  const revenue = computeRevenueMicrousd(s)
  if (revenue <= 0) return null
  return computeProfitMicrousd(s) / revenue
}

/** Tailwind color class for a signed dollar value: green > 0, red < 0, gray = 0. */
export function profitColorClass(value: number): string {
  if (value > 0) return 'text-emerald-600 dark:text-emerald-400'
  if (value < 0) return 'text-destructive'
  return 'text-muted-foreground'
}

/**
 * Format a margin fraction as a percent string with one decimal, e.g.
 * `0.4231 → "42.3%"`, `null → "—"`. Negatives keep their sign.
 */
export function formatMarginPercent(fraction: number | null): string {
  if (fraction === null) return '—'
  return `${(fraction * 100).toFixed(1)}%`
}
